/**
 * The words the selection bar shows for a component's raw metadata: property names (`isMultiple`),
 * option values (`bottom right`, `bottomRight`, `Bottom Right`), event names (`onClick`) and curated
 * labels (`Menu Items`). One rule, sentence case, so every chip, list row, tooltip and aria-label
 * reads the same way whatever casing the component set happened to use. Display only: the value
 * written back to the property is always the raw option.
 *
 * Culture-invariant on purpose — only `toLowerCase` / `toUpperCase`, never the `toLocale*` forms.
 */

/** Short words that read as initialisms, not words. */
const ACRONYMS: ReadonlySet<string> = new Set<string>([
  "api",
  "css",
  "csv",
  "html",
  "id",
  "json",
  "kb",
  "mb",
  "pdf",
  "qr",
  "svg",
  "tl",
  "ui",
  "uri",
  "url",
  "x",
  "xml",
  "y"
]);

/**
 * Lowercase tokens that are codes, not words: Col breakpoints (`lg 8 · md 12`) and bare time-format
 * tokens (`ss`, `mm`). A general rule cannot tell them from words, so they are listed.
 */
const VERBATIM_TOKENS: ReadonlySet<string> = new Set<string>(["xs", "sm", "md", "lg", "xl", "xxl", "hh", "mm", "ss"]);

/** Misspellings baked into raw metadata names, corrected for display only. */
const TYPOS: ReadonlyMap<string, string> = new Map<string, string>([
  ["seperator", "separator"],
  ["seperators", "separators"],
  ["seperate", "separate"],
  ["adress", "address"],
  ["heigth", "height"],
  ["lenght", "length"],
  ["widht", "width"]
]);

/**
 * Property and event names the rule cannot make good — a boolean phrased as a question, a name that
 * describes the implementation rather than what the user picks, or a group member whose group already
 * says the rest ("Columns" → Desktop / Tablet / Phone). Keyed by the raw name.
 */
const NAME_OVERRIDES: ReadonlyMap<string, string> = new Map<string, string>([
  ["isThousandSeperatorOn", "Thousand separator"],
  ["isMultiple", "Multiple files"],
  ["uploadTarget", "Upload to"],
  ["dataSource", "Data"],
  ["defaultDataField", "Data field"],
  ["dynamicBackGroundColor", "Background color"],
  ["imageFit", "Fit"],
  ["iconName", "Icon"],
  ["allowClear", "Clear button"],
  ["showSearch", "Search"],
  ["maxTagCount", "Max tags"],
  ["decimalScale", "Decimals"],
  ["footerOptions", "Footer"],
  ["menuOpeningType", "Open on"],
  ["hideOnMobileResolution", "Hide on mobile"],
  ["colspan", "Column span"],
  ["rowspan", "Row span"],
  ["desktopColumnsCount", "Desktop"],
  ["tabletColumnsCount", "Tablet"],
  ["phoneColumnsCount", "Phone"],
  ["horizontalGutter", "Horizontal"],
  ["verticalGutter", "Vertical"],
  ["onClickEvent", "On event click"]
]);

/**
 * Option values the rule cannot make good: compressed formatter ids (`money-var-1`), language codes,
 * and run-together words. Keyed by the raw value.
 */
const VALUE_OVERRIDES: ReadonlyMap<string, string> = new Map<string, string>([
  ["readonly", "Read only"],
  ["datetime", "Date and time"],
  ["date-var-1", "Date (alt)"],
  ["money-var-1", "Money (alt)"],
  ["money-4-decimal", "Money, 4 decimals"],
  ["money-tl", "Money (TL)"],
  ["fractional-2", "2 decimals"],
  ["fractional-5", "5 decimals"],
  ["en", "English"],
  ["tr", "Turkish"]
]);

/** Characters that only occur in formats, units, lists and codes (`hh:mm`, `image/*`, `.pdf,.png`, `#fff`, `50%`). */
const FORMAT_CHARACTERS: RegExp = /[:/*,#%()@]/;

/**
 * True for text that is a format or a code and must read exactly as stored: no lowercase letter at all
 * (`CODE128`, `DD/MM/YYYY`, `D M Y`, `24`), a leading digit / dot / sign (`12px`, `.pdf`), a format
 * character, or a listed token.
 */
function isVerbatim(raw: string): boolean {
  if (VERBATIM_TOKENS.has(raw)) {
    return true;
  }
  if (!/[a-z]/.test(raw)) {
    return true;
  }
  if (/^[0-9.+-]/.test(raw)) {
    return true;
  }
  return FORMAT_CHARACTERS.test(raw);
}

/** `isMultiple` → `Multiple`, `hasHeader` → `Header`; `issue` / `hash` are left alone. */
function stripBooleanPrefix(raw: string): string {
  const match: RegExpMatchArray | null = raw.match(/^(is|has)([A-Z].*)$/);
  return match ? match[2] : raw;
}

/** camelCase / PascalCase / kebab / snake / dot / space → lowercase words. `HTMLContent` → html, content. */
function splitWords(raw: string): string[] {
  return raw
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .split(/[\s_.-]+/)
    .filter((word: string): boolean => word.length > 0)
    .map((word: string): string => word.toLowerCase());
}

function displayWord(word: string, isFirst: boolean): string {
  const fixed: string = TYPOS.get(word) ?? word;
  if (ACRONYMS.has(fixed)) {
    return fixed.toUpperCase();
  }
  return isFirst ? fixed.charAt(0).toUpperCase() + fixed.slice(1) : fixed;
}

/** The sentence-case rule shared by names and values, without overrides. */
function sentenceCase(raw: string): string {
  const trimmed: string = raw.trim();
  if (isVerbatim(trimmed)) {
    return trimmed;
  }
  const words: string[] = splitWords(stripBooleanPrefix(trimmed));
  return words.map((word: string, index: number): string => displayWord(word, index === 0)).join(" ");
}

/**
 * A property, event, group or style name as the bar shows it: `globalSearch` → "Global search",
 * `onClick` → "On click", `projectId` → "Project ID", `Menu Items` → "Menu items". Idempotent.
 */
export function toDisplayLabel(raw: string): string {
  return NAME_OVERRIDES.get(raw) ?? sentenceCase(raw);
}

/**
 * An option value as the bar shows it: `bottomRight` / `bottom-right` / `Bottom Right` → "Bottom right",
 * `DD/MM/YYYY` and `.pdf` unchanged. Only the label — the stored value stays the raw option.
 */
export function toValueLabel(raw: string): string {
  return VALUE_OVERRIDES.get(raw) ?? sentenceCase(raw);
}

/**
 * A display label placed mid-sentence ("Add radio button", "Decrease font size"): its first letter
 * lowered unless the first word is an initialism ("Search URL", not "Search uRL").
 */
export function toMidSentence(label: string): string {
  const firstWord: string = label.split(" ")[0] ?? "";
  if (firstWord !== "" && firstWord === firstWord.toUpperCase() && ACRONYMS.has(firstWord.toLowerCase())) {
    return label;
  }
  return label.charAt(0).toLowerCase() + label.slice(1);
}
